import { KeyboardEvent, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';
import type { TellerUser } from '~/models/teller-user';
import noImage from '@app/_assets/no-image.png';
import { Link } from '@app/_components/link';
import { styled, Image, HStack, Paragraph } from '@styled-system/jsx';
import type { PostCommentFormData } from './type';

type User = Pick<TellerUser, 'thumbnail' | 'name'>;

type Props = {
  requireLogin: boolean;
  loginPagePath: string;
  user?: User;
  placeholder?: string;
  onCommandEnterSubmit?: () => void;
};

export const CommentInputArea = ({
  requireLogin,
  loginPagePath,
  user,
  placeholder,
  onCommandEnterSubmit,
}: Props) => {
  const handleOnKeyDown = useCallback(
    (keyEvent: KeyboardEvent) => {
      // Enter + Ctrl or Enter + Cmd
      if (keyEvent.key === 'Enter' && (keyEvent.ctrlKey || keyEvent.metaKey)) {
        onCommandEnterSubmit?.();
      }
    },
    [onCommandEnterSubmit]
  );
  const { register } = useFormContext<PostCommentFormData>();

  return (
    <InputContainer>
      <Image
        src={user?.thumbnail ?? noImage}
        alt={user?.name}
        w="32px"
        h="32px"
        borderRadius="50%"
        flexShrink="0"
        mb="3px"
      />

      {/* input comment */}
      {requireLogin ? (
        <Paragraph
          fontSize="size_14"
          color="text_secondary"
          flexShrink="0"
          mb="10px"
          flex="1"
          lineHeight="1"
        >
          コメントをするには
          <Link href={loginPagePath} color="tint">
            ログイン
          </Link>
        </Paragraph>
      ) : (
        <TextArea
          maxRows={15}
          overflow="hidden"
          placeholder={placeholder}
          onKeyDown={handleOnKeyDown}
          {...register('commentText')}
        />
      )}
    </InputContainer>
  );
};

const TextArea = styled(TextareaAutosize, {
  base: {
    flex: 1,
    lineHeight: '1.375',
    verticalAlign: 'top',
    fontSize: {
      base: 'size_16',
      lg: 'size_14',
    },
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    outline: '0',
    border: 'none',
    appearance: 'none',
    resize: 'none',
    backgroundColor: 'transparent',
    alignItems: 'flex-end',
  },
});

const InputContainer = styled(HStack, {
  base: {
    w: '100%',
    minH: '40px',
    flex: 1,
    minW: '0%',
    position: 'relative',
    borderRadius: '20px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderImage: 'initial',
    bg: 'surface_secondary',
    gap: '2',
    pl: '1',
    pr: '2',
    alignItems: 'flex-end',
  },
});
